import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";
import emojiRegex from "emoji-regex";
import { v4 as uuidv4 } from "uuid";

import { makeStyles, withStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import IconButton from "@material-ui/core/IconButton";
import { i18n } from "../../translate/i18n";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CheckIcon from "@material-ui/icons/CheckCircle";
import ReplayIcon from "@material-ui/icons/Replay";
import ClearOutlinedIcon from "@material-ui/icons/ClearOutlined";
import api from "../../services/api";

import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

import facebookIcon from "../../assets/facebook.png";
import insatagramIcon from "../../assets/instagram.png";
import whatsappIcon from "../../assets/whatsapp.png";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const useStyles = makeStyles((theme) => ({
    ticket: {
        position: "relative",
        margin: "8px 10px",
        width: "calc(100% - 20px)",
        borderRadius: 18,
        minHeight: 86,
        overflow: "hidden",
        background: theme.mode === "light"
            ? "linear-gradient(145deg, #FFFFFF 0%, #F8FAFF 100%)"
            : "linear-gradient(145deg, rgba(15,23,42,.96), rgba(2,6,23,.94))",
        border: theme.mode === "light"
            ? "1px solid rgba(15,23,42,.08)"
            : "1px solid rgba(148,163,184,.10)",
        boxShadow: theme.mode === "light"
            ? "0 10px 26px rgba(15,23,42,.07)"
            : "0 14px 34px rgba(0,0,0,.24)",
        transition: "all .2s ease",
        "&:hover": {
            transform: "translateY(-1px)",
            borderColor: theme.mode === "light"
                ? "rgba(37,99,235,.28)"
                : "rgba(96,165,250,.36)",
            boxShadow: theme.mode === "light"
                ? "0 14px 34px rgba(37,99,235,.12)"
                : "0 18px 42px rgba(37,99,235,.16)",
            background: theme.mode === "light"
                ? "linear-gradient(145deg, #FFFFFF 0%, #EEF6FF 100%)"
                : "linear-gradient(145deg, rgba(30,64,175,.22), rgba(15,23,42,.96))",
        },
        "&.Mui-selected": {
            background: theme.mode === "light"
                ? "linear-gradient(145deg, rgba(37,99,235,.12), rgba(255,255,255,1))"
                : "linear-gradient(145deg, rgba(37,99,235,.26), rgba(124,58,237,.10))",
            borderColor: "rgba(96,165,250,.42)",
        },
        [theme.breakpoints.down("sm")]: {
            margin: "10px 14px",
            width: "calc(100% - 28px)",
            borderRadius: 18,
            minHeight: 118,
            padding: "10px 8px",
            background: "#FFFFFF",
            border: "1px solid #E5EAF2",
            boxShadow: "0 10px 26px rgba(15,23,42,.08)",
        },
    },

    pendingTicket: {
        cursor: "unset",
    },

    noTicketsDiv: {
        display: "flex",
        height: "100px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },

    noTicketsText: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: "14px",
        lineHeight: "1.4",
    },

    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },

    contactNameWrapper: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 8,
        minWidth: 0,
    },

    premiumContactName: {
        fontWeight: 900,
        fontSize: "1rem",
        color: theme.mode === "light" ? "#0F172A" : "#F8FAFC",
        maxWidth: "calc(100% - 80px)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },

    premiumStatusLine: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        flexWrap: "wrap",
        marginTop: 6,
    },

    premiumStatusBadge: {
        height: 20,
        padding: "0 7px",
        borderRadius: 999,
        fontSize: "0.62rem",
        fontWeight: 900,
        display: "inline-flex",
        alignItems: "center",
        lineHeight: "20px",
    },

    premiumTime: {
        fontSize: "0.78rem",
        fontWeight: 900,
        color: theme.mode === "light" ? "#64748B" : "#94A3B8",
        whiteSpace: "nowrap",
    },

    lastMessageTime: {
        position: "static",
        marginRight: 0,
    },

    closedBadge: {
        alignSelf: "center",
        justifySelf: "flex-end",
        marginRight: 32,
        marginLeft: "auto",
    },

    contactLastMessage: {
        paddingRight: 8,
        color: theme.mode === "light" ? "#64748B" : "#94A3B8",
        fontSize: "0.88rem",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },

    newMessagesCount: {
        alignSelf: "center",
        marginRight: 8,
        marginLeft: "auto",
        "& .MuiBadge-badge": {
            background: "linear-gradient(135deg, #EF4444, #F97316)",
            color: "#fff",
            fontWeight: 900,
            boxShadow: "0 8px 18px rgba(239,68,68,.25)",
        },
    },

    bottomButton: {
        top: "12px",
        [theme.breakpoints.down("sm")]: {
            position: "absolute",
            right: 10,
            bottom: 10,
            top: "auto",
            width: 32,
            height: 32,
            padding: 4,
            background: "#F8FAFC",
            border: "1px solid #E2E8F0",
            boxShadow: "0 6px 14px rgba(15,23,42,.10)",
        },
    },

    badgeStyle: {
        color: "white",
        backgroundColor: green[500],
    },

    acceptButton: {
        position: "absolute",
        left: "50%",
    },

    ticketQueueColor: {
        flex: "none",
        width: "4px",
        height: "70%",
        position: "absolute",
        top: "15%",
        left: "0%",
        borderRadius: "0 999px 999px 0",
        boxShadow: "0 0 18px currentColor",
    },

    userTag: {
        background: theme.mode === "light"
            ? "rgba(37,99,235,.08)"
            : "rgba(37,99,235,.16)",
        color: theme.mode === "light" ? "#2563EB" : "#93C5FD",
        border: "1px solid rgba(37,99,235,.18)",
        padding: "4px 9px",
        borderRadius: 999,
        fontSize: "0.62rem",
        fontWeight: 900,
        maxWidth: 95,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    divTags: {
        marginTop: 7,
        flexWrap: "wrap",
        display: "flex",
        flexDirection: "row",
        gap: 4,
        justifyContent: "flex-start",
        alignItems: "center",
    },
    tags: {
        color: "#FFF",
        border: "1px solid rgba(255,255,255,.18)",
        padding: "4px 9px",
        borderRadius: 999,
        fontSize: "0.72rem",
        fontWeight: 900,
        textAlign: "center",
        boxShadow: "0 6px 14px rgba(0,0,0,.12)",
    },
    divUser: {
        position: "absolute",
        marginRight: 0,
        left: 0,
        top: 0,
        flexWrap: "wrap",
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
    },
    user: {
        color: "#eee",
        border: "1px solid #CCC",
        padding: 0,
        paddingLeft: 5,
        paddingRight: 5,
        borderRadius: 0,
        fontSize: "0.6em",
        textAlign: "center",
    },

    tagsWrapper: {
        zIndex: 500,
    },

    badge: {
        backgroundColor: "#44b700",
        color: "#44b700",
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
        "&::after": {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            animation: "$ripple 1.2s infinite ease-in-out",
            border: "1px solid currentColor",
            content: '""',
        },
    },
    "@keyframes ripple": {
        "0%": {
            transform: "scale(.8)",
            opacity: 1,
        },
        "100%": {
            transform: "scale(2.4)",
            opacity: 0,
        },
    },
}));

const SmallAvatar = withStyles((theme) => ({
    root: {
        width: 22,
        height: 22,
        border: `2px solid ${theme.palette.background.paper}`,
    },
}))(Avatar);

const getAvatarChannel = (channel) => {
    if (channel === "facebook") {
        return facebookIcon;
    }

    if (channel === "whatsapp") {
        return whatsappIcon;
    }

    if (channel === "whatsappapi") {
        return whatsappIcon;
    }

    if (channel === "instagram") {
        return insatagramIcon;
    }
};

const TicketListItem = ({ ticket }) => {
    const classes = useStyles();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const { ticketId } = useParams();
    const isMounted = useRef(true);
    const { user } = useContext(AuthContext);
    const { setCurrentTicket, setTabOpen } = useContext(TicketsContext);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    function getRatingIcon(rate) {
        let icon = "";
        if (rate === 1) {
            icon = "😡";
        } else if (rate === 2) {
            icon = "😠";
        } else if (rate === 3) {
            icon = "😐";
        } else if (rate === 4) {
            icon = "😃";
        } else if (rate === 5) {
            icon = "😍";
        }

        return icon;
    }


    const handleAcepptTicket = async (ticket) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, {
                status: "open",
                userId: user?.id,
            });
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }
        history.push(`/tickets/${ticket.uuid}`);
    };

    const handleAcepptTicketBot = async (ticket) => {
        // alert("Bot");
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, {
                status: "open",
                userId: user?.id,
            });
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }
        history.push(`/tickets/${ticket.uuid}`);
    };

    const handleReopenTicket = async (ticket) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, {
                status: "open",
                userId: user?.id,
            });
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }
        history.push(`/tickets/${ticket.uuid}`);
    };

    const handleViewTicket = async (ticket) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, {
                status: "pending",
            });
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }
        history.push(`/tickets/${ticket.uuid}`);
    };

    const handleSelectTicket = (ticket) => {
        const code = uuidv4();
        const { id, uuid } = ticket;
        setTabOpen(ticket.status)
        setCurrentTicket({ id, uuid, code });
    };

    const handleClosedTicket = async (ticket) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${ticket.id}`, {
                status: "closed",
            });
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }
    };

    const renderUserName = (name) => {
        let str = name.replace(emojiRegex(), "").trim();

        const firstName = str.split(" ")[0];

        return firstName;
    };

    const getStatusMeta = () => {
        if (ticket.status === "pending") {
            return {
                label: ticket.isBot ? "IA atendendo" : "Aguardando IA",
                color: "#7C3AED",
                bg: "rgba(124,58,237,.14)"
            };
        }

        if (ticket.status === "open") {
            return {
                label: "Atendimento humano",
                color: "#22C55E",
                bg: "rgba(34,197,94,.13)"
            };
        }

        if (ticket.status === "group" || ticket.isGroup) {
            return {
                label: "Grupo",
                color: "#F59E0B",
                bg: "rgba(245,158,11,.13)"
            };
        }

        if (ticket.status === "closed") {
            return {
                label: "Finalizado",
                color: "#64748B",
                bg: "rgba(100,116,139,.14)"
            };
        }

        return {
            label: ticket.status || "Ticket",
            color: "#2563EB",
            bg: "rgba(37,99,235,.14)"
        };
    };

    const statusMeta = getStatusMeta();

    return (
        <React.Fragment key={ticket.id}>
            <ListItem
                dense
                button
                onClick={(e) => {
                    handleSelectTicket(ticket)
                }}
                selected={ticketId && +ticketId === ticket.id}
                className={clsx(classes.ticket, {
                    [classes.pendingTicket]: ticket.status === "pending",
                })}
            >
                <Tooltip
                    arrow
                    placement="right"
                    title={ticket.queue?.name || "Sem departamento"}
                >
                    <span
                        style={{
                            backgroundColor: ticket.queue?.color || "#7C7C7C",
                        }}
                        className={classes.ticketQueueColor}
                    ></span>
                </Tooltip>
                <ListItemAvatar>
                    <Badge
                        overlap="circular"
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        badgeContent={
                            <SmallAvatar
                                alt={ticket?.channel}
                                src={getAvatarChannel(ticket?.channel)}
                            />
                        }
                    >
                        <Avatar
                            alt={ticket?.contact?.name}
                            src={ticket?.contact?.urlPicture}
                            style={{
                                width: 50,
                                height: 50,
                                fontWeight: 900,
                                background: statusMeta.color,
                                boxShadow: `0 0 0 3px ${statusMeta.bg}, 0 12px 26px rgba(0,0,0,.22)`
                            }}
                        />
                    </Badge>
                </ListItemAvatar>
                <ListItemText
                    disableTypography
                    primary={
                        <span className={classes.contactNameWrapper}>
                            <Typography
                                noWrap
                                component="span"
                                variant="body2"
                                className={classes.premiumContactName}
                            >
                                {ticket.contact.name}
                            </Typography>

                            {ticket.lastMessage && (
                                <Typography
                                    className={classes.premiumTime}
                                    component="span"
                                    variant="body2"
                                >
                                    {isSameDay(
                                        parseISO(ticket.updatedAt),
                                        new Date()
                                    ) ? (
                                        <>
                                            {format(
                                                parseISO(ticket.updatedAt),
                                                "HH:mm"
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {format(
                                                parseISO(ticket.updatedAt),
                                                "dd/MM/yyyy"
                                            )}
                                        </>
                                    )}
                                </Typography>
                            )}
                        </span>
                    }
                    secondary={
                        <span className={classes.contactNameWrapper}>
                            {ticket.status === "closed"
                                ? ticket?.userRating
                                    ? getRatingIcon(ticket?.userRating?.rate)
                                    : null
                                : null}
                            <Typography
                                className={classes.contactLastMessage}
                                noWrap
                                component="span"
                                variant="body2"
                                color="textSecondary"
                            >
                                {ticket.lastMessage ? (
                                    <MarkdownWrapper>
                                        {ticket.lastMessage}
                                    </MarkdownWrapper>
                                ) : (
                                    <br />
                                )}
                            </Typography>

                            <Badge
                                overlap="rectangular"
                                className={classes.newMessagesCount}
                                badgeContent={ticket.unreadMessages}
                                classes={{
                                    badge: classes.badgeStyle,
                                }}
                            />
                        </span>
                    }
                />
                {ticket.status === "pending" && (
                    <IconButton
                        className={classes.bottomButton}
                        color="primary"
                        loading={loading.toString()}
                        onClick={(e) =>
                            ticket.isBot
                                ? handleAcepptTicketBot(ticket)
                                : handleAcepptTicket(ticket)
                        }
                    >
                        <CheckIcon />
                    </IconButton>
                )}
                {/* {ticket.status === "pending" && (
                    <IconButton
                        className={classes.bottomButton}
                        color="primary"
                        onClick={(e) => handleViewTicket(ticket)}
                    >
                        <VisibilityIcon />
                    </IconButton>
                )} */}
                <div className={classes.premiumStatusLine}>
                    <span
                        className={classes.premiumStatusBadge}
                        style={{
                            background: statusMeta.bg,
                            color: statusMeta.color,
                            border: `1px solid ${statusMeta.color}33`
                        }}
                    >
                        {statusMeta.label}
                    </span>

                    {ticket.whatsappId && (
                        <span
                            className={classes.userTag}
                            title={i18n.t("ticketsList.connectionTitle")}
                        >
                            {ticket.whatsapp?.name}
                        </span>
                    )}

                    {ticket.queue?.name && (
                        <span
                            className={classes.userTag}
                            style={{
                                color: ticket.queue?.color || statusMeta.color,
                                borderColor: `${ticket.queue?.color || statusMeta.color}33`
                            }}
                        >
                            {ticket.queue?.name}
                        </span>
                    )}
                </div>

                <div className={classes.divTags}>
                    {ticket.isGroup && (
                        <div className={classes.tagsWrapper}>
                            <div
                                key={ticket.id}
                                className={classes.tags}
                                // title={ticket.isGroup}
                                style={{
                                    backgroundColor: "#7C7C7C",
                                }}
                            >
                                Grupo
                            </div>
                        </div>
                    )}
                    {ticket.user?.id &&
                        user.profile.toUpperCase() === "ADMIN" && (
                            <div className={classes.tagsWrapper}>
                                <div
                                    key={ticket.user.id}
                                    className={classes.tags}
                                    title={renderUserName(ticket.user.name)}
                                    style={{
                                        backgroundColor:
                                            ticket.user.color === "" ||
                                                !ticket.user.color
                                                ? "#7C7C7C"
                                                : ticket.user.color,
                                    }}
                                >
                                    {renderUserName(ticket.user.name)}
                                </div>
                            </div>
                        )}
                    {ticket.tags?.length > 0 && (
                        <>
                            <div className={classes.tagsWrapper}>
                                <div
                                    key={ticket.tags[0].id}
                                    className={classes.tags}
                                    title={ticket.tags[0].name}
                                    style={{
                                        backgroundColor: ticket.tags[0].color,
                                    }}
                                >
                                    {ticket.tags[0].name}
                                </div>
                            </div>
                            {ticket.tags.length > 1 && (
                                <div
                                    key={ticket.tags[1].id}
                                    className={classes.tags}
                                    title={ticket.tags[1].name}
                                    style={{
                                        backgroundColor: ticket.tags[1].color,
                                    }}
                                >
                                    +{ticket.tags.length - 1}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {(ticket.status === "pending" && (user.showDashboard === "enabled" || user.profile === "admin")) && (
                    <IconButton
                        className={classes.bottomButton}
                        color="primary"
                        onClick={(e) => handleClosedTicket(ticket)}
                    >
                        <ClearOutlinedIcon />
                    </IconButton>
                )}
                {ticket.status === "open" && (
                    <IconButton
                        className={classes.bottomButton}
                        color="primary"
                        onClick={(e) => handleViewTicket(ticket)}
                    >
                        <ReplayIcon />
                    </IconButton>
                )}
                {ticket.status === "open" && (
                    <IconButton
                        className={classes.bottomButton}
                        color="primary"
                        onClick={(e) => handleClosedTicket(ticket)}
                    >
                        <ClearOutlinedIcon />
                    </IconButton>
                )}
                {ticket.status === "closed" && (
                    <IconButton
                        className={classes.bottomButton}
                        color="primary"
                        onClick={(e) => handleReopenTicket(ticket)}
                    >
                        <ReplayIcon />
                    </IconButton>
                )}
                {ticket.status === "closed" && (
                    <IconButton
                        className={classes.bottomButton}
                        color="primary"
                    ></IconButton>
                )}
            </ListItem>
            <Divider variant="inset" component="li" style={{ display: "none" }} />
        </React.Fragment>
    );
};

export default TicketListItem;
